/**
 * 1mind.ai "superhuman" launcher-script loader (GWP-12151).
 *
 * 1mind is an AI-powered GTM chat vendor deployed via an external "launcher"
 * script tag (`https://launcher.1mind.com/<deploymentId>`). Each persona is a
 * distinct deployment id. There is no generic script-loader util in wac-web, so
 * this mirrors the established vendor pattern (see
 * `libraries/intercom/src/init.ts`) with Marketo-style idempotency
 * (see `use-marketo-form.ts`): inject at most once per deployment id, client
 * side only, and remove the tag on teardown.
 *
 * The two persona deployment ids for the JPD product-discovery A/B/C test:
 *  - aiGuide: "AI Guide" (waveform variant)
 *  - stephen: "Stephen"  (human-persona variant)
 */

export const ONE_MIND_LAUNCHER_ORIGIN = 'https://launcher.1mind.com';

/**
 * Deployment ids supplied by the 1mind vendor (Kelli Meador, 2026-07-17).
 * The control cell has no launcher — it keeps the existing chat experience.
 */
export const ONE_MIND_DEPLOYMENTS = {
	aiGuide: 'deployment-rprgpn5ltf',
	stephen: 'deployment-fhn85kw52t',
} as const;

export type OneMindDeploymentId = (typeof ONE_MIND_DEPLOYMENTS)[keyof typeof ONE_MIND_DEPLOYMENTS];

/**
 * Module-level guard so the same deployment is never injected twice even if a
 * consumer remounts within the same page lifetime (React StrictMode double
 * effects, provider re-mounts, etc.).
 */
const injectedDeployments = new Set<string>();

export const getOneMindScriptId = (deploymentId: string): string =>
	`onemind-launcher-${deploymentId}`;

export interface InjectOneMindLauncherOptions {
	/** Called once the vendor launcher script has successfully loaded. */
	onLoad?: () => void;
	/** Called if the vendor launcher script fails to load (blocked, 4xx/5xx, offline). */
	onError?: () => void;
}

/**
 * Injects the 1mind launcher script for the given deployment id. Idempotent and
 * a no-op during SSR (guards on `document`).
 *
 * `onLoad` / `onError` let callers instrument whether the third-party widget
 * actually rendered for a given experiment cell. This matters because a silent
 * vendor failure would otherwise be indistinguishable from "the variant had no
 * effect" when the experiment is analysed.
 */
export const injectOneMindLauncher = (
	deploymentId: string,
	{ onLoad, onError }: InjectOneMindLauncherOptions = {},
): void => {
	if (typeof document === 'undefined' || !deploymentId) {
		return;
	}

	const scriptId = getOneMindScriptId(deploymentId);
	if (injectedDeployments.has(deploymentId) || document.getElementById(scriptId)) {
		injectedDeployments.add(deploymentId);
		return;
	}

	const script = document.createElement('script');
	script.id = scriptId;
	script.async = true;
	script.src = `${ONE_MIND_LAUNCHER_ORIGIN}/${deploymentId}`;
	if (onLoad) {
		script.addEventListener('load', onLoad);
	}
	if (onError) {
		script.addEventListener('error', onError);
	}
	document.body.appendChild(script);
	injectedDeployments.add(deploymentId);
};

/**
 * Returns true when a `postMessage` originated from a 1mind-controlled origin.
 * The launcher is served from `launcher.1mind.com` but the widget itself may be
 * framed from another `*.1mind.com` subdomain, so both are accepted.
 */
export const isOneMindOrigin = (origin: string): boolean => {
	if (origin === ONE_MIND_LAUNCHER_ORIGIN) {
		return true;
	}

	try {
		const { protocol, hostname } = new URL(origin);
		return protocol === 'https:' && (hostname === '1mind.com' || hostname.endsWith('.1mind.com'));
	} catch {
		return false;
	}
};

/**
 * Extracts a stable event name from a vendor `postMessage` payload.
 *
 * NOTE: the exact 1mind message schema is not documented in the integration
 * guide, so this reads the common `type` / `event` / `name` keys rather than
 * assuming one. Whatever string the vendor sends is forwarded verbatim as an
 * analytics attribute, which means we capture real engagement signal on day one
 * and can pin the metric definition to the observed value afterwards.
 */
export const getOneMindMessageName = (data: unknown): string | undefined => {
	if (typeof data === 'string') {
		return data || undefined;
	}

	if (typeof data !== 'object' || data === null) {
		return undefined;
	}

	const record = data as Record<string, unknown>;
	const candidate = record['type'] ?? record['event'] ?? record['name'];

	return typeof candidate === 'string' && candidate ? candidate : undefined;
};

/**
 * Subscribes to 1mind widget `postMessage` events. Returns an unsubscribe fn.
 * No-op (returns a noop) during SSR.
 */
export const subscribeToOneMindMessages = (
	onMessage: (eventName: string) => void,
): (() => void) => {
	if (typeof window === 'undefined') {
		return () => {};
	}

	const handleMessage = (event: MessageEvent) => {
		if (!isOneMindOrigin(event.origin)) {
			return;
		}

		const eventName = getOneMindMessageName(event.data);
		if (eventName) {
			onMessage(eventName);
		}
	};

	window.addEventListener('message', handleMessage);
	return () => window.removeEventListener('message', handleMessage);
};

/**
 * Best-effort teardown: removes the injected launcher <script> tag so a
 * subsequent client-side (SPA) navigation away from the experiment page does
 * not re-run it. NOTE: the vendor script may create its own DOM/iframe/globals
 * that this does not remove; full teardown needs the vendor's documented
 * shutdown API (as Intercom exposes `shutdown`). Tracked as a vendor follow-up.
 */
export const removeOneMindLauncher = (deploymentId: string): void => {
	if (typeof document === 'undefined' || !deploymentId) {
		return;
	}

	const existing = document.getElementById(getOneMindScriptId(deploymentId));
	existing?.parentNode?.removeChild(existing);
	injectedDeployments.delete(deploymentId);
};
